import React, {useEffect, useMemo, useState} from 'react'
import {Dropdown, OverlayTrigger, Tooltip} from 'react-bootstrap'
import Select from 'react-select'
import {Formik, Form} from 'formik'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import {InterpreterAiQcFilterValues} from './interpreterAiQcApi'

type Props = {
  initialValues: InterpreterAiQcFilterValues
  onApply: (values: InterpreterAiQcFilterValues) => void
  onReset: () => void
}

const statusOptions = [
  {value: 'Pass', label: 'Pass'},
  {value: 'Fail', label: 'Fail'},
]

const checklistOptions = [
  {value: 'Mentioned', label: 'Mentioned'},
  {value: 'Missed', label: 'Missed'},
]

const isAnyFilterSelected = (values: InterpreterAiQcFilterValues) =>
  Boolean(
    values.startDate ||
      values.endDate ||
      values.aiQcStatus ||
      values.nameCheck ||
      values.languageCheck ||
      values.idCheck
  )

const InterpreterAiQcFilterDropdown: React.FC<Props> = ({initialValues, onApply, onReset}) => {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  useEffect(() => {
    setIsFilterApplied(isAnyFilterSelected(initialValues))
  }, [initialValues])

  const dropdownId = useMemo(
    () => `interpreter-ai-qc-filter-${Math.random().toString(36).slice(2)}`,
    []
  )

  return (
    <Dropdown onToggle={(next) => setShowDropdownMenu(next)}>
      <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-ai-qc-filter'>Filter</Tooltip>}>
        <Dropdown.Toggle
          variant='primary'
          id={dropdownId}
          className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
        >
          <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
        </Dropdown.Toggle>
      </OverlayTrigger>

      {showDropdownMenu && (
        <Dropdown.Menu className='p-4 w-350px' align='end'>
          <div className='px-4 pb-4'>
            <div className='fs-4 text-dark fw-semibolder'>Filter</div>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values, {setSubmitting}) => {
              setSubmitting(true)
              onApply(values)
              setSubmitting(false)
              setShowDropdownMenu(false)
            }}
          >
            {({values, setFieldValue, handleSubmit, resetForm, dirty, isSubmitting}) => (
              <Form
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault()
                  handleSubmit()
                }}
              >
                <div className='separator border-gray-200'></div>
                <div className='card px-4 mt-4'>
                  <div className='row flex-column'>
                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Start Date</label>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        value={values.startDate || ''}
                        onChange={(e) => setFieldValue('startDate', e.target.value)}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>End Date</label>
                      <input
                        type='date'
                        className='form-control form-control-sm'
                        value={values.endDate || ''}
                        onChange={(e) => setFieldValue('endDate', e.target.value)}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>AI QC Status</label>
                      <Select
                        classNamePrefix='react-select'
                        isClearable
                        options={statusOptions}
                        value={statusOptions.find((o) => o.value === values.aiQcStatus) ?? null}
                        onChange={(opt: {value: string} | null) =>
                          setFieldValue('aiQcStatus', opt?.value ?? '')
                        }
                        placeholder='All'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Name</label>
                      <Select
                        classNamePrefix='react-select'
                        isClearable
                        options={checklistOptions}
                        value={checklistOptions.find((o) => o.value === values.nameCheck) ?? null}
                        onChange={(opt: {value: string} | null) =>
                          setFieldValue('nameCheck', opt?.value ?? '')
                        }
                        placeholder='All'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>Language</label>
                      <Select
                        classNamePrefix='react-select'
                        isClearable
                        options={checklistOptions}
                        value={
                          checklistOptions.find((o) => o.value === values.languageCheck) ?? null
                        }
                        onChange={(opt: {value: string} | null) =>
                          setFieldValue('languageCheck', opt?.value ?? '')
                        }
                        placeholder='All'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
                      />
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='form-label fs-7 mb-1'>ID</label>
                      <Select
                        classNamePrefix='react-select'
                        isClearable
                        options={checklistOptions}
                        value={checklistOptions.find((o) => o.value === values.idCheck) ?? null}
                        onChange={(opt: {value: string} | null) =>
                          setFieldValue('idCheck', opt?.value ?? '')
                        }
                        placeholder='All'
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
                      />
                    </div>
                  </div>
                </div>

                <div className='text-end pt-4 px-4'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light me-2'
                    onClick={() => {
                      resetForm()
                      onReset()
                      setShowDropdownMenu(false)
                    }}
                  >
                    Reset
                  </button>
                  <button type='submit' className='btn btn-sm btn-primary' disabled={isSubmitting || !dirty}>
                    Apply
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dropdown.Menu>
      )}
    </Dropdown>
  )
}

export default InterpreterAiQcFilterDropdown
